import React from 'react';
import {
  Box,
  Typography,
  Container,
} from '@mui/material';

export default function Wallet() {
  const ledger = [
    { name: 'Nova Electronics', meta: 'Redeemed · Gold card', amt: 180 },
    { name: 'Wanderlust Travel', meta: 'Redeemed · Gold card', amt: 640 },
    { name: 'Kirana Fresh', meta: 'Redeemed · Silver card', amt: 90 },
    { name: 'Glow Beauty Co.', meta: 'Pending settlement', amt: 120 },
    { name: 'Urban Threads', meta: 'Redeemed · Gold card', amt: 210 },
  ];

  const features = [
    {
      num: '01',
      title: 'Instant ledger entry',
      description: 'The moment you redeem, a pending line appears with the exact assured amount.',
    },
    {
      num: '02',
      title: 'Tier-based settlement',
      description: 'All coupon cashback amounts are settled within 48 hours — no card ever waits longer.',
    },
    {
      num: '03',
      title: 'One-tap withdrawal',
      description: 'Move your balance to any UPI ID or bank account once it clears, no minimum threshold.',
    },
  ];

  return (
    <Box
      component="section"
      id="wallet"
      sx={{
        padding: '80px 0',
        background: 'transparent',
        position: 'relative',
        textAlign: 'left',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.1fr' },
            gap: { xs: '40px', md: '64px' },
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Left Column: Phone Wallet Screen Mockup */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              className="phone"
              sx={{
                width: '290px',
                background: '#153A35',
                border: '1px solid rgba(245, 241, 230, 0.14)',
                borderRadius: '34px',
                padding: '14px',
                boxShadow: '0 20px 50px -20px rgba(0,0,0,0.5)',
              }}
            >
              <Box
                className="phone-screen"
                sx={{
                  background: '#FAF3E6',
                  color: '#0F2E2B',
                  borderRadius: '22px',
                  padding: '20px 16px',
                  minHeight: '480px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Screen Header */}
                <Box
                  sx={{
                    display: 'flex',
                    justify: 'space-between',
                    alignItems: 'center',
                    marginBottom: '18px',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '11px',
                      color: 'rgba(15, 46, 43, 0.55)',
                      fontWeight: 600,
                    }}
                  >
                    ISORET WALLET
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '11px',
                      color: 'rgba(15, 46, 43, 0.55)',
                      fontWeight: 600,
                    }}
                  >
                    ●●●● 4821
                  </Typography>
                </Box>

                {/* Balance Display */}
                <Box sx={{ marginBottom: '20px' }}>
                  <Typography
                    sx={{
                      fontSize: '11.5px',
                      color: 'rgba(15, 46, 43, 0.55)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      fontWeight: 700,
                    }}
                  >
                    Available cashback
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Fraunces", serif',
                      fontWeight: 800,
                      fontSize: '34px',
                      color: '#1C6758',
                      lineHeight: 1.2,
                    }}
                  >
                    ₹2,140.00
                  </Typography>
                </Box>

                {/* Ledger Entries */}
                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  {ledger.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        justify: 'space-between',
                        alignItems: 'center',
                        padding: '12px 0',
                        borderTop: '1px dashed rgba(15, 46, 43, 0.2)',
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0F2E2B' }}>
                          {item.name}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: 'rgba(15, 46, 43, 0.5)' }}>
                          {item.meta}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '13px',
                          fontWeight: 700,
                          color: '#1C6758',
                        }}
                      >
                        +{item.amt}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Right Column: Wallet Info Copy */}
          <Box>
            <Box sx={{ maxWidth: { xs: '100%', md: '480px' }, margin: { xs: '0 auto 0 0', md: '0' } }}>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '13px',
                  color: '#E8C078',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginBottom: '16px',
                }}
              >
                Your wallet
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  fontSize: { xs: '28px', md: '36px' },
                  color: '#F5F1E6',
                  marginBottom: '16px',
                  lineHeight: 1.2,
                }}
              >
                Watch every rupee arrive — with a receipt.
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(245, 241, 230, 0.66)',
                  fontSize: '16px',
                  marginBottom: '24px',
                }}
              >
                No vague "processing" states. Each redemption creates a ledger line the moment it's confirmed, so you always know what's pending and what's landed.
              </Typography>

              {/* Wallet Features List */}
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 32px 0',
                }}
              >
                {features.map((feature, idx) => (
                  <Box
                    component="li"
                    key={idx}
                    sx={{
                      display: 'flex',
                      gap: '14px',
                      padding: '14px 0',
                      borderTop: idx === 0 ? 'none' : '1px solid rgba(245, 241, 230, 0.14)',
                      fontSize: '14.5px',
                      color: 'rgba(245, 241, 230, 0.66)',
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        color: '#E8C078',
                        flexShrink: 0,
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      {feature.num}
                    </Typography>
                    <Box>
                      <Typography
                        component="b"
                        sx={{
                          color: '#F5F1E6',
                          fontWeight: 600,
                          display: 'block',
                          marginBottom: '4px',
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <span>{feature.description}</span>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
